import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { useAnnotatorsSelection } from './useAnnotatorsSelection';
import { AnnotatorResponse } from '../../types/response';

// TODO: cleanup with cp_annotation_createproject_agent ff
// The agent chip is not a selection: it never enters selectedAnnotators and is stripped from
// every change before the diff.
const ANNOTATION_AGENT_CHIP_ID = 'annotation-agent-chip';

export const AnnotatorsSelection = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const {
    selectedAnnotators,
    handleRemoveAnnotator,
    annotators,
    annotatorInputValue,
    lastTypedAnnotatorValue,
    setAnnotatorInputValue,
    handleAddAnnotator,
    isAnnotatorsLoading,
    isAgentOn,
  } = useAnnotatorsSelection();

  // TODO: cleanup with cp_annotation_createproject_agent ff
  const agentChip: AnnotatorResponse = {
    id: ANNOTATION_AGENT_CHIP_ID,
    label: formatMessage({ id: 'createProjectWizard.step.allocation.agent.label' }),
  };
  const renderTagsWithAgent = (
    value: AnnotatorResponse[],
    getTagProps: AutocompleteRenderGetTagProps
  ) =>
    value.map((option, index) => {
      const { key, onDelete, ...tagProps } = getTagProps({ index });
      return option.id === ANNOTATION_AGENT_CHIP_ID ? (
        <Chip
          key={key}
          {...tagProps}
          icon={<SmartToyOutlinedIcon fontSize="small" />}
          label={option.label}
          sx={{ '& .MuiChip-icon': { color: theme.palette.primary.main } }}
        />
      ) : (
        <Chip key={key} {...tagProps} onDelete={onDelete} label={option.label} />
      );
    });

  return (
    <Box id="annotators-selection">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        <Typography variant="h6" sx={{ mb: theme.spacing(4) }}>
          {formatMessage({ id: 'createProjectWizard.step.allocation.selectAnnotators.title' })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          *
        </Typography>
      </Box>
      <Autocomplete
        multiple
        disabled={isAnnotatorsLoading ?? false}
        options={annotators}
        value={isAgentOn ? [agentChip, ...selectedAnnotators] : selectedAnnotators}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        inputValue={annotatorInputValue}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            lastTypedAnnotatorValue.current = newInputValue;
            setAnnotatorInputValue(newInputValue);
          }
        }}
        onChange={(event, newValue) => {
          const people = newValue.filter((n) => n.id !== ANNOTATION_AGENT_CHIP_ID);
          const added = people.filter((n) => !selectedAnnotators.some((a) => a.id === n.id));

          const removed = selectedAnnotators.filter((a) => !people.some((n) => n.id === a.id));

          added.forEach(handleAddAnnotator);
          removed.forEach(handleRemoveAnnotator);
        }}
        getOptionDisabled={(option) => selectedAnnotators.some((a) => a.id === option.id)}
        renderTags={isAgentOn ? renderTagsWithAgent : undefined}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;

          return (
            <Box
              key={key}
              component="li"
              {...otherProps}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Checkbox checked={selected} />

              <span>{option.label}</span>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={formatMessage({
              id: 'createProjectWizard.step.allocation.selectAnnotators.placeholder',
            })}
          />
        )}
      />
    </Box>
  );
};
